import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import Select from "react-select";
import CommonToaster from "../Common/CommonToaster";
import { useBranch } from "../../contexts/BranchContext";

const headerStyle = {
  background: "white",
  fontWeight: 600,
  borderBottom: "none",
  fontSize: 17,
};

const InstructorForm = ({
  initialData,
  loading = false,
  toast,
  setToast,
  mode = "add",
  onSubmit,
  onCancel,
}) => {
  const [form, setForm] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const { branches, isMultiBranch } = useBranch();

  useEffect(() => {
    if(toast?.show && toast?.type === "error") {
      setTimeout(() => {
        setSaving(false);
      }, 1200);
    }
  }, [toast]);

  useEffect(() => {
    setForm(initialData);
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    let updatedForm = { ...form };
    if (type === "checkbox") {
      updatedForm[name] = checked;
    } else if (type === "file") {
      updatedForm[name] = files[0];
    } else {
      updatedForm[name] = value;
    }
    setForm(updatedForm);
  };

  const handleSubmit = (e) => {
    setSaving(true);
    e.preventDefault();
    onSubmit(form);
  };

  const handleClear = () => setForm(initialData);

  const getDateValue = (val) => {
  if (!val) return "";
  // If it's already YYYY-MM-DD, just return
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  // Try to extract the first 10 chars (YYYY-MM-DD from ISO)
  return val.slice(0, 10);
};


  return (
    <Form className="bg-transparent" onSubmit={handleSubmit}>
      {/* Personal Details */}
      <Card className="mb-4">
        <Card.Header style={headerStyle}>
          <i className="fa fa-user me-2 text-success" /> Personal Details
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Full Name *</Form.Label>
                <Form.Control
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Email Address *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Mobile Number *</Form.Label>
                <Form.Control
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Date of Birth</Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={getDateValue(form.dob)}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Gender</Form.Label>
                <Form.Select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Profile Picture</Form.Label>
                <Form.Control
                  type="file"
                  name="avatar"
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  rows={2}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Professional Details */}
      <Card className="mb-4">
        <Card.Header style={headerStyle}>
          <i className="fa fa-briefcase me-2 text-success" /> Professional Details
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Highest Qualification</Form.Label>
                <Form.Select
                  name="qualification"
                  value={form.qualification}
                  onChange={handleChange}
                >
                  <option value="">Select Qualification</option>
                  <option>Bachelor's</option>
                  <option>Master's</option>
                  <option>PhD</option>
                  <option>Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Years of Experience</Form.Label>
                <Form.Control
                  name="experience"
                  value={form.experience}
                  onChange={handleChange}
                  type="number"
                  min={0}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Primary Subject / Skill</Form.Label>
                <Form.Control
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Other Skills</Form.Label>
                <Form.Control
                  name="otherSkills"
                  value={form.otherSkills}
                  onChange={handleChange}
                  placeholder="React, Node.js, Python (comma-separated)"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>LinkedIn / Social Profile Link</Form.Label>
                <Form.Control
                  name="linkedin"
                  value={form.linkedin}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/username"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Joined Date</Form.Label>
                <Form.Control
                  type="date"
                  name="joinedDate"
                  value={getDateValue(form.joinedDate)}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Other Details */}
      <Card className="mb-4">
        <Card.Header style={headerStyle}>
          <i className="fa fa-info-circle me-2 text-success" /> Other Details
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label>About / Bio</Form.Label>
                <Form.Control
                  as="textarea"
                  name="about"
                  value={form.about}
                  onChange={handleChange}
                  rows={3}
                  maxLength={300}
                  placeholder="Brief description about the instructor (max 300 characters)"
                />
                <div className="text-end text-muted" style={{ fontSize: 13 }}>
                  {form.about?.length || 0}/300 characters
                </div>
              </Form.Group>
            </Col>
            <Col xs={12} className="d-flex align-items-center">
              <Form.Check
                type="switch"
                name="active"
                label="Active Status"
                checked={form.active}
                onChange={handleChange}
              />
              <span className="ms-2 text-muted">Enable instructor account upon creation</span>
            </Col>
            {isMultiBranch && branches.length > 0 && (
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Branches</Form.Label>
                  <Select
                    isMulti
                    options={branches.filter(b => b.isActive).map(b => ({ value: b._id, label: b.name }))}
                    value={branches.filter(b => (form.branches || []).includes(b._id)).map(b => ({ value: b._id, label: b.name }))}
                    onChange={(selected) => setForm(prev => ({ ...prev, branches: (selected || []).map(s => s.value) }))}
                    placeholder="Assign to branches..."
                    classNamePrefix="bf-rs"
                    isClearable
                    styles={{
                      control: (base) => ({ ...base, backgroundColor: "var(--input-bg)", borderColor: "var(--border-primary)", borderRadius: "8px", minHeight: "38px", "&:hover": { borderColor: "var(--text-tertiary)" } }),
                      menu: (base) => ({ ...base, backgroundColor: "var(--card-bg)", border: "1px solid var(--border-primary)", borderRadius: "8px", boxShadow: "0 4px 12px var(--card-shadow)" }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "var(--bg-hover)" : "transparent", color: state.isSelected ? "#fff" : "var(--text-primary)" }),
                      singleValue: (base) => ({ ...base, color: "var(--text-primary)" }),
                      multiValue: (base) => ({ ...base, backgroundColor: "var(--bg-secondary)" }),
                      multiValueLabel: (base) => ({ ...base, color: "var(--text-primary)" }),
                      input: (base) => ({ ...base, color: "var(--text-primary)" }),
                    }}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Buttons */}
      <Row className="mb-4">
        <Col xs={12} className="d-flex justify-content-end gap-2">
          {onCancel && (
            <Button variant="light" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant="light" type="button" onClick={handleClear}>
            Clear / Reset
          </Button>
          <Button
            variant="success"
            type="submit"
            className="d-flex align-items-center"
            disabled={loading || saving}
          >
            {mode === "edit" ? "Save Changes" : "Save Instructor"}
          </Button>
        </Col>
      </Row>

      <CommonToaster
        show={toast?.show}
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </Form>
  );
};

export default InstructorForm;
